import { useEffect, useState } from 'react'
import { getAssets, removeAsset } from 'src/routes/Asset/usePendingArmStatus'
import { Asset } from 'src/types'
import { distanceNotification, getAsset } from 'src/util/notification'

export const useGeolocation = (enabled: boolean): [number, number] => {
  const [position, setPosition] = useState<[number, number]>(null)
  useEffect(() => {
    if (enabled) {
      const opts = { enableHighAccuracy: true, maximumAge: 600000 }
      let lastCall = 0
      // eslint-disable-next-line no-undef
      const handleSuccess: PositionCallback = ({ coords }) => {
        setPosition([coords.latitude, coords.longitude])
        // Throttle checkAssets calls to 2min
        const now = Date.now()
        if (now - lastCall >= 2 * 60 * 1000) {
          lastCall = now
          checkAssets(coords)
        }
      }
      // eslint-disable-next-line no-undef
      const handleError: PositionErrorCallback = () => setPosition(null)
      const wid = navigator.geolocation.watchPosition(handleSuccess, handleError, opts)
      return () => {
        navigator.geolocation.clearWatch(wid)
        setPosition(null)
      }
    }
  }, [enabled])

  return position
}

const maxArmDistance = 0.3 // Km
const maxOutRange = 0.2 // Km

/**
 * Check assets in localStorage that are out of range and notify if not laterArm
 * @param position Current user position
 */
// eslint-disable-next-line no-undef
const checkAssets = async (position: GeolocationCoordinates): Promise<void> => {
  const pendingAssets = getAssets()
  if (!pendingAssets.length) return

  const outRangeAssets = pendingAssets.filter(({ assetLoc }) => {
    if (!assetLoc) return false
    return isOutRange(assetLoc, position)
  })

  // Check if outRangeAssets are armed right now (by us or other user)
  const promises = outRangeAssets.map(async ({ assetId, subscriberCode, dateArm }) =>
    await getAsset(assetId, subscriberCode, dateArm)
  )

  const assetsData = await Promise.all(promises)

  assetsData
    .filter(({ laterArm }) => laterArm === false)
    .forEach(({ active }) =>
      distanceNotification(active)
    )
  assetsData
    .forEach(({ active }) =>
      removeAsset(active.slug)
    )
}

const calculateDistance = (assetLoc: [string, string], loc: number[]): number => {
  const toRad = (x: number): number => x * Math.PI / 180
  const [lat, lon] = assetLoc

  const lat1 = parseFloat(lat)
  const lon1 = parseFloat(lon)
  const lat2 = loc[0]
  const lon2 = loc[1]
  const R = 6371 // Km

  const x1 = lat2 - lat1
  const dLat = toRad(x1)
  const x2 = lon2 - lon1
  const dLon = toRad(x2)
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const dist = R * c
  return dist
}

// eslint-disable-next-line no-undef
export const isOutRange = (assetLoc: [string, string], loc: GeolocationCoordinates) => {
  if (!loc) return false
  const { latitude, longitude } = loc
  const dist = calculateDistance(assetLoc, [latitude, longitude])
  return dist > maxOutRange
}

export const isNearby = (asset: Asset, loc: number[]): boolean => {
  if (!loc) return false
  const { lat, lon } = asset
  const dist = calculateDistance([lat, lon], loc)

  return dist < maxArmDistance
}
